import { APP_ROUTES } from "utils/appRoutes";
import { USER_CATEGORY } from "utils/userCategoryEnum";
import { supabase } from "../lib/supabase";
import { authService } from "../services/authService";

export function generateRandomPassword(length = 12) {
  const charset =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*";
  const array = new Uint32Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (num) => charset[num % charset.length]).join("");
}

// ─────────────────────────────────────────────────────────────────────────────
// ROLE ENFORCEMENT HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const getActiveGlobalMemberships = async (userId) => {
  const { data, error } = await supabase
    .from("user_org_memberships")
    .select("id, organization_id, user_category_id, is_active")
    .eq("user_id", userId)
    .eq("is_active", 0);

  if (error) throw error;
  return data || [];
};

const checkCrossOrgRolePermission = async (
  userId,
  targetOrgId,
  requestedCategoryId,
) => {
  if (requestedCategoryId === USER_CATEGORY.SME) {
    return { allowed: true, reason: null };
  }

  const activeMemberships = await getActiveGlobalMemberships(userId);

  const conflictingMembership = activeMemberships.find(
    (m) =>
      m.organization_id !== targetOrgId &&
      (m.user_category_id === USER_CATEGORY.ADMIN ||
        m.user_category_id === USER_CATEGORY.MEMBER),
  );

  if (conflictingMembership) {
    return {
      allowed: false,
      reason:
        "This user already holds an Admin or Member role in another organization. " +
        "They can only be added as an SME in this organization.",
    };
  }

  return { allowed: true, reason: null };
};

const getExistingMembershipInOrg = async (userId, organizationId) => {
  const { data, error } = await supabase
    .from("user_org_memberships")
    .select("id, user_category_id, is_active")
    .eq("user_id", userId)
    .eq("organization_id", organizationId)
    .maybeSingle();

  if (error) throw error;
  return data || null;
};

// ─────────────────────────────────────────────────────────────────────────────
// REACTIVATE
// ─────────────────────────────────────────────────────────────────────────────

const reactivateMembership = async (
  existingMembership,
  personnelData,
  userId,
) => {
  const requestedCategoryId =
    personnelData.user_category_id || USER_CATEGORY.MEMBER;

  if (requestedCategoryId === USER_CATEGORY.ADMIN) {
    throw new Error(
      "An inactive user cannot be reactivated as Admin in the same organization. " +
      "The user should log in and create a new organization to become an admin.",
    );
  }

  const { allowed, reason } = await checkCrossOrgRolePermission(
    userId,
    personnelData.organization_id,
    requestedCategoryId,
  );
  if (!allowed) throw new Error(reason);

  const { error: membershipError } = await supabase
    .from("user_org_memberships")
    .update({
      is_active: 0,
      user_category_id: requestedCategoryId,
      updated_at: new Date().toISOString(),
    })
    .eq("id", existingMembership.id);

  if (membershipError) throw membershipError;

  const { error: personnelError } = await supabase
    .from("personnel")
    .update({
      is_active: 0,
      name: personnelData.name,
      phone: personnelData.phone,
      department_id: personnelData.department_id || null,
      experience_level_id: personnelData.experience_level_id || null,
      group_id: personnelData.group_id,
      role_id: personnelData.role_id,
      level_id: personnelData.level_id || null, // ← was position
      skills: personnelData.skills || [],
      certifications: personnelData.certifications || [],
      avatar_url: personnelData.photo || "",
      collaborator_start_date: personnelData.collaborator_start_date || null,
      collaborator_end_date: personnelData.collaborator_end_date || null,
      updated_at: new Date().toISOString(),
    })
    .eq("membership_id", existingMembership.id);

  if (personnelError) throw personnelError;

  await supabase
    .from("user_profiles")
    .update({
      group_id: personnelData.group_id || null,
      role_id: personnelData.role_id || null,
      level_id: personnelData.level_id || null, // ← was position
      updated_at: new Date().toISOString(),
    })
    .eq("id", userId);

  console.log("Reactivated membership for:", personnelData.email);
  return { reactivated: true, user: { id: userId } };
};

// ─────────────────────────────────────────────────────────────────────────────
// ROLE SWITCH
// ─────────────────────────────────────────────────────────────────────────────

export const switchUserRoleInOrg = async (
  userId,
  organizationId,
  newCategoryId,
) => {
  try {
    const { allowed, reason } = await checkCrossOrgRolePermission(
      userId,
      organizationId,
      newCategoryId,
    );
    if (!allowed) throw new Error(reason);

    const { error: membershipError } = await supabase
      .from("user_org_memberships")
      .update({
        user_category_id: newCategoryId,
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId)
      .eq("organization_id", organizationId);

    if (membershipError) throw membershipError;

    console.log(
      `Role switched for user ${userId} in org ${organizationId} → ${newCategoryId}`,
    );
    return true;
  } catch (error) {
    console.error("Error switching role:", error);
    throw error;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// FETCH PERSONNEL
// ─────────────────────────────────────────────────────────────────────────────

export const fetchPersonnel = async () => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) throw new Error("User not authenticated");

    const allowedRoutes = JSON.parse(
      localStorage.getItem("allowedRoutes") || "[]",
    );
    const canViewAllMembers = allowedRoutes.includes(
      APP_ROUTES.ALL_MEMBER_LIST_PERMISSION,
    );

    const userProfile = await authService.getUserProfile(user.id);
    const selectedOrgId =
      localStorage.getItem("selectedOrgId") || userProfile?.organization_id;

    if (!selectedOrgId) throw new Error("Organization ID not found for user");

    if (canViewAllMembers) {
      const { data: memberships, error: membershipError } = await supabase
        .from("user_org_memberships")
        .select(`id, user_id, organization_id, user_category_id, personnel (*)`)
        .eq("is_active", 0)
        .eq("organization_id", selectedOrgId);

      if (membershipError) throw membershipError;

      const personnelList = (memberships || [])
        .filter((m) => m.personnel)
        .map((m) => ({
          ...m.personnel,
          user_id: m.user_id,
          organization_id: m.organization_id,
          user_category_id: m.user_category_id,
        }));

      const enrichedPersonnel =
        await enrichWithEmailConfirmation(personnelList);

      return enrichedPersonnel.sort((a, b) => {
        if (a.user_id === user.id) return -1;
        if (b.user_id === user.id) return 1;
        const nameA = a.name || "";
        const nameB = b.name || "";
        return nameA.localeCompare(nameB);
      });
    }

    const { data: memberships, error } = await supabase
      .from("user_org_memberships")
      .select(`id, user_id, organization_id, user_category_id, personnel (*)`)
      .eq("user_id", user.id)
      .eq("organization_id", selectedOrgId);

    if (error) throw error;

    const personnelList = (memberships || [])
      .filter((m) => m.personnel)
      .map((m) => ({
        ...m.personnel,
        user_id: m.user_id,
        organization_id: m.organization_id,
        user_category_id: m.user_category_id,
      }));

    const enrichedPersonnel = await enrichWithEmailConfirmation(personnelList);
    return enrichedPersonnel.sort((a, b) => {
      if (a.user_id === user.id) return -1;
      if (b.user_id === user.id) return 1;
      const nameA = a.name || "";
      const nameB = b.name || "";
      return nameA.localeCompare(nameB);
    });
  } catch (error) {
    console.error("Error fetching personnel:", error);
    throw error;
  }
};

export const fetchPersonnelByUserIdList = async (userCategoryMap) => {
  try {
    const userIds = Object.keys(userCategoryMap);

    const { data: memberships, error: membershipError } = await supabase
      .from("user_org_memberships")
      .select(`id, user_id, user_category_id, personnel (*)`)
      .in("user_id", userIds);

    if (membershipError) throw membershipError;

    const personnelData = memberships
      .filter((m) => m.personnel)
      .map((m) => ({
        ...m.personnel,
        user_id: m.user_id,
        user_category_id: m.user_category_id,
      }));

    return await enrichWithEmailConfirmation(personnelData);
  } catch (error) {
    console.error("Error fetching personnel by user ID list:", error);
    throw error;
  }
};

export const fetchPersonnelById = async (personnelId) => {
  try {
    const { data, error } = await supabase
      .from("personnel")
      .select("*")
      .eq("id", personnelId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error fetching personnel by ID:", error);
    throw error;
  }
};

const enrichWithEmailConfirmation = async (personnelList) => {
  const userIds = personnelList.map((p) => p.user_id).filter(Boolean);
  if (!userIds.length) return personnelList;

  const { data: confirmationData, error } = await supabase.rpc(
    "get_users_email_confirmation",
    { user_ids: userIds },
  );

  if (error) {
    console.error("Error fetching email confirmation:", error);
    return personnelList;
  }

  const confirmationMap = Object.fromEntries(
    (confirmationData || []).map((u) => [u.id, u.email_confirmed_at]),
  );

  return personnelList.map((item) => ({
    ...item,
    email_confirmed_at: confirmationMap[item.user_id] ?? null,
  }));
};

// ─────────────────────────────────────────────────────────────────────────────
// CREATE PERSONNEL
// ─────────────────────────────────────────────────────────────────────────────

export const createPersonnel = async (personnelData) => {
  console.log("PERSONNEL", personnelData);
  try {
    const { data: existingProfile, error: existingProfileError } =
      await supabase
        .from("user_profiles")
        .select("id")
        .eq("email", personnelData.email)
        .maybeSingle();

    if (existingProfileError) throw existingProfileError;

    const { data: org, error: orgError } = await supabase
      .from("organizations")
      .select("*")
      .eq("id", personnelData.organization_id)
      .single();
    if (orgError) throw orgError;

    let data;

    if (existingProfile) {
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        "handle_membership_entry",
        {
          p_email: personnelData.email,
          p_full_name: personnelData.name,
          p_user_category_id: personnelData.user_category_id,
          p_org_id: personnelData.organization_id,
          p_org_name: org?.name,
          p_group_id: personnelData.group_id,
          p_role_id: personnelData.role_id,
          p_level_id: personnelData.level_id || null, // ← was p_position
          p_department_id: personnelData.department_id || null,
          p_experience_id: personnelData.experience_level_id || null,
          p_phone: personnelData.phone,
          p_collaborator_start_date:
            personnelData.collaborator_start_date || null,
          p_collaborator_end_date: personnelData.collaborator_end_date || null,
          p_avatar_url: personnelData.avatar_url || "",
        },
      );

      if (rpcError || !rpcData?.success) {
        throw new Error(rpcData?.message || rpcError?.message);
      }

      data = rpcData;
    } else {
      const generatedPassword = generateRandomPassword(12);
      const { data: signUpData, error: signUpError } =
        await supabase.auth.signUp({
          email: personnelData.email,
          password: generatedPassword,
          options: {
            data: {
              full_name: personnelData.name,
              user_category_id: personnelData.user_category_id,
              org_name: org?.name,
              org_id: personnelData.organization_id,
              group_id: personnelData.group_id,
              group_role: personnelData.role_id,
              level_id: personnelData.level_id ?? null,
              department_id: personnelData.department_id || null,
              experience_id: personnelData.experience_level_id || null,
              phone: personnelData.phone,
              collaborator_start_date:
                personnelData.collaborator_start_date || null,
              collaborator_end_date:
                personnelData.collaborator_end_date || null,
              avatar_url: personnelData.avatar_url || "",
              temp_password: generatedPassword,
            },
          },
        });

      if (signUpError) throw signUpError;
      data = signUpData;

      let userProfile = null;
      for (let attempt = 0; attempt < 5; attempt++) {
        const { data: profile } = await supabase
          .from("user_profiles")
          .select("id")
          .eq("email", personnelData.email)
          .maybeSingle();

        if (profile) {
          userProfile = profile;
          break;
        }
        await new Promise((r) => setTimeout(r, 800));
      }

      if (!userProfile) {
        console.error(
          "user_profiles row not found after signup for:",
          personnelData.email,
        );
        throw new Error("The system is taking longer than usual to initialize this profile. Please wait a moment and try saving again.");
      }

      let personnelRow = null;
      for (let attempt = 0; attempt < 5; attempt++) {
        const { data: pRow } = await supabase
          .from("personnel")
          .select("id")
          .eq("membership_id", userProfile.id)
          .maybeSingle();

        if (pRow) {
          personnelRow = pRow;
          break;
        }
        await new Promise((r) => setTimeout(r, 800));
      }

      if (!personnelRow) {
        const { data: membership } = await supabase
          .from("user_org_memberships")
          .select("id")
          .eq("user_id", userProfile.id)
          .eq("organization_id", personnelData.organization_id)
          .maybeSingle();

        if (membership) {
          for (let attempt = 0; attempt < 5; attempt++) {
            const { data: pRow } = await supabase
              .from("personnel")
              .select("id")
              .eq("membership_id", membership.id)
              .maybeSingle();

            if (pRow) {
              personnelRow = pRow;
              break;
            }
            await new Promise((r) => setTimeout(r, 800));
          }
        }
      }

      if (!personnelRow) {
        console.error("personnel row not found for user:", userProfile.id);
        throw new Error("The system is taking longer than usual to initialize this profile. Please try saving again");
      }

      const draftId = personnelData.orgDocumentsDraftId;
      if (!draftId) {
        console.log(
          "No orgDocumentsDraftId — skipping resume link (manual profile)",
        );
        return data;
      }

      const { data: draftDoc, error: draftDocError } = await supabase
        .from("org_documents_draft")
        .select("*")
        .eq("id", draftId)
        .single();

      if (draftDocError || !draftDoc) {
        console.error(
          "org_documents_draft fetch failed:",
          draftDocError?.message,
        );
        return data;
      }

      const fileExt = draftDoc.file_name.split(".").pop().toLowerCase();

      const { data: moveResult, error: moveError } =
        await supabase.functions.invoke("resume-temp-to-main", {
          body: {
            tempPath: draftDoc.temp_path,
            orgId: personnelData.organization_id,
            personnelId: personnelRow.id,
            fileExt,
          },
        });

      if (moveError || !moveResult?.finalPath) {
        console.error(
          "move-resume edge function failed:",
          moveError?.message || moveResult?.error,
        );
        return data;
      }

      const contentType =
        fileExt === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

      const { error: resumeError } = await supabase.from("resumes").insert({
        personnel_id: personnelRow.id,
        file_name: draftDoc.file_name,
        file_path: moveResult.finalPath,
        file_size: draftDoc.file_size,
        file_type: contentType,
      });

      if (resumeError) {
        console.error("resumes insert failed:", resumeError.message);
        return data;
      }

      await deleteTempMember(draftId);

      await supabase
        .from("org_documents_draft")
        .update({ status: "CONFIRMED" })
        .eq("id", draftDoc.id);

      console.log("Resume linked successfully for:", personnelData.email);
    }

    return data;
  } catch (error) {
    console.error("Error creating personnel:", error);
    throw error;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// ADD EXISTING USER TO ORG
// ─────────────────────────────────────────────────────────────────────────────

const addExistingUserToOrg = async (existingUserId, personnelData) => {
  const requestedCategoryId =
    personnelData.user_category_id || USER_CATEGORY.MEMBER;

  const { data: membership, error: membershipError } = await supabase
    .from("user_org_memberships")
    .insert({
      user_id: existingUserId,
      organization_id: personnelData.organization_id,
      user_category_id: requestedCategoryId,
      is_active: 0,
    })
    .select("id")
    .single();

  if (membershipError) throw membershipError;

  const { data: personnelRow, error: personnelError } = await supabase
    .from("personnel")
    .insert({
      name: personnelData.name,
      email: personnelData.email,
      phone: personnelData.phone,
      level_id: personnelData.level_id || null, // ← was position
      department_id: personnelData.department_id || null,
      experience_level_id: personnelData.experience_level_id || null,
      group_id: personnelData.group_id,
      role_id: personnelData.role_id,
      skills: personnelData.skills || [],
      certifications: personnelData.certifications || [],
      avatar_url: personnelData.photo || "",
      collaborator_start_date: personnelData.collaborator_start_date || null,
      collaborator_end_date: personnelData.collaborator_end_date || null,
      membership_id: membership.id,
      is_active: 0,
    })
    .select("id")
    .single();

  if (personnelError) throw personnelError;

  await linkResumeIfPresent(personnelData, personnelRow.id);

  return { user: { id: existingUserId } };
};

// ─────────────────────────────────────────────────────────────────────────────
// SHARED HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const isEmailExistsError = (err) =>
  err.code === "email_exists" ||
  err.status === 422 ||
  err.message?.toLowerCase().includes("already been registered") ||
  err.message?.toLowerCase().includes("already registered") ||
  err.message?.toLowerCase().includes("email_exists");

const linkResumeIfPresent = async (personnelData, personnelId) => {
  const draftId = personnelData.orgDocumentsDraftId;
  if (!draftId) {
    console.log("No orgDocumentsDraftId — skipping resume link");
    return;
  }

  const { data: draftDoc, error: draftDocError } = await supabase
    .from("org_documents_draft")
    .select("*")
    .eq("id", draftId)
    .single();

  if (draftDocError || !draftDoc) {
    console.error("org_documents_draft fetch failed:", draftDocError?.message);
    return;
  }

  const fileExt = draftDoc.file_name.split(".").pop().toLowerCase();

  const { data: moveResult, error: moveError } =
    await supabase.functions.invoke("resume-temp-to-main", {
      body: {
        tempPath: draftDoc.temp_path,
        orgId: personnelData.organization_id,
        personnelId,
        fileExt,
      },
    });

  if (moveError || !moveResult?.finalPath) {
    console.error(
      "move-resume edge function failed:",
      moveError?.message || moveResult?.error,
    );
    return;
  }

  const contentType =
    fileExt === "pdf"
      ? "application/pdf"
      : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

  const { error: resumeError } = await supabase.from("resumes").insert({
    personnel_id: personnelId,
    file_name: draftDoc.file_name,
    file_path: moveResult.finalPath,
    file_size: draftDoc.file_size,
    file_type: contentType,
  });

  if (resumeError) {
    console.error("resumes insert failed:", resumeError.message);
    return;
  }

  await deleteTempMember(draftId);

  await supabase
    .from("org_documents_draft")
    .update({ status: "CONFIRMED" })
    .eq("id", draftDoc.id);

  console.log("Resume linked successfully for:", personnelData.email);
};

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE PERSONNEL
// ─────────────────────────────────────────────────────────────────────────────

export const updatePersonnel = async (personnelId, personnelData) => {
  try {
    const personnelUpdate = {
      name: personnelData.name,
      email: personnelData.email,
      phone: personnelData.phone,
      department_id: personnelData.department_id || null,
      experience_level_id: personnelData.experience_level_id || null,
      skills: personnelData.skills ?? [],
      certifications: personnelData.certifications ?? [],
      avatar_url: personnelData.photo ?? personnelData.avatar_url ?? "",
      bio_content: personnelData.education ?? personnelData.bio_content ?? "",
      collaborator_start_date: personnelData.collaborator_start_date ?? null,
      collaborator_end_date: personnelData.collaborator_end_date ?? null,
      group_id: personnelData.group_id ?? null,
      role_id: personnelData.role_id ?? null,
      level_id: personnelData.level_id ?? null, // ← was position
      updated_at: new Date().toISOString(),
    };

    const personnelPromise = supabase
      .from("personnel")
      .update(personnelUpdate)
      .eq("id", personnelId)
      .select()
      .single();

    const profileFields = {
      group_id: personnelData.group_id ?? null,
      role_id: personnelData.role_id ?? null,
      level_id: personnelData.level_id ?? null, // ← was position
      updated_at: new Date().toISOString(),
    };

    const hasProfileChanges = ["group_id", "role_id", "level_id"].some(
      (key) => personnelData[key] !== undefined,
    );

    const profilePromise = hasProfileChanges
      ? (async () => {
        const { data: personnel, error: fetchErr } = await supabase
          .from("personnel")
          .select("membership_id")
          .eq("id", personnelId)
          .single();

        if (fetchErr) throw fetchErr;
        if (!personnel?.membership_id) return null;

        const { data: membership, error: memErr } = await supabase
          .from("user_org_memberships")
          .select("user_id")
          .eq("id", personnel.membership_id)
          .single();

        if (memErr) throw memErr;

        const { data, error: profileErr } = await supabase
          .from("user_profiles")
          .update(profileFields)
          .eq("id", membership.user_id)
          .select()
          .single();

        if (profileErr) throw profileErr;
        return data;
      })()
      : Promise.resolve(null);

    const [{ data: updatedPersonnel, error: personnelErr }, updatedProfile] =
      await Promise.all([personnelPromise, profilePromise]);

    if (personnelErr) throw personnelErr;

    return { personnel: updatedPersonnel, profile: updatedProfile };
  } catch (error) {
    console.error("Error updating personnel:", error);
    throw error;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// DELETE PERSONNEL (soft delete)
// ─────────────────────────────────────────────────────────────────────────────

export const deletePersonnel = async (personnelId) => {
  try {
    const { data: personnelData, error: fetchError } = await supabase
      .from("personnel")
      .select("membership_id")
      .eq("id", personnelId)
      .single();

    if (fetchError) throw fetchError;

    const { error: personnelError } = await supabase
      .from("personnel")
      .update({ is_active: 1 })
      .eq("id", personnelId);

    if (personnelError) throw personnelError;

    if (personnelData?.membership_id) {
      const { error: membershipError } = await supabase
        .from("user_org_memberships")
        .update({ is_active: 1 })
        .eq("id", personnelData.membership_id);

      if (membershipError) throw membershipError;
    }

    return true;
  } catch (error) {
    console.error("Error deleting personnel:", error);
    throw error;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// PERSONNEL PROFILES (bio versions)
// ─────────────────────────────────────────────────────────────────────────────

export const fetchPersonnelProfiles = async (personnelId) => {
  try {
    const { data, error } = await supabase
      .from("personnel_profiles")
      .select("*")
      .eq("personnel_id", personnelId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error fetching personnel profiles:", error);
    throw error;
  }
};

export const createPersonnelProfile = async (personnelId, profileData) => {
  try {
    const { data, error } = await supabase
      .from("personnel_profiles")
      .insert([
        {
          personnel_id: personnelId,
          profile_name: profileData.name,
          bio_content: profileData.content,
          skills: profileData.skills || [],
          certifications: profileData.certifications || [],
          is_default: profileData.is_default || false,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error creating personnel profile:", error);
    throw error;
  }
};

export const updatePersonnelProfile = async (profileId, profileData) => {
  try {
    const updateData = {
      profile_name: profileData.name || profileData.profile_name,
      bio_content: profileData.content || profileData.bio_content,
      skills: profileData.skills || [],
      certifications: profileData.certifications || [],
      is_default: profileData.is_default || false,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from("personnel_profiles")
      .update(updateData)
      .eq("id", profileId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating personnel profile:", error);
    throw error;
  }
};

export const deletePersonnelProfile = async (profileId) => {
  try {
    const { error } = await supabase
      .from("personnel_profiles")
      .delete()
      .eq("id", profileId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error deleting personnel profile:", error);
    throw error;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// TRANSFORM HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export const transformPersonnelToComponent = (dbPersonnel, profiles = []) => {
  return {
    id: dbPersonnel.id,
    name: dbPersonnel.name,
    role: dbPersonnel.title,
    email: dbPersonnel.email,
    phone: dbPersonnel.phone,
    photo: dbPersonnel.avatar_url,
    alt: `Profile photo of ${dbPersonnel.name}`,
    skills: dbPersonnel.skills || [],
    department_id: dbPersonnel.department_id ?? "",

    experience_level_id: dbPersonnel.experience_level_id ?? "",
    yearsExperience: dbPersonnel.years_experience || 0,
    groupId: dbPersonnel.group_id ?? null,
    roleId: dbPersonnel.role_id ?? null,
    level_id: dbPersonnel.level_id ?? null, // ← was position
    email_confirmed_at: dbPersonnel.email_confirmed_at ?? null,
    bioVersions: profiles.map((p) => ({
      id: p.id,
      name: p.profile_name,
      content: p.bio_content,
      lastUpdated: new Date(p.updated_at).toISOString().split("T")[0],
    })),
    certifications: dbPersonnel.certifications || [],
    education: dbPersonnel.bio_content || "",
    projectHistory: [],
    resumes: [],
    collaborator_start_date: dbPersonnel.collaborator_start_date || null,
    collaborator_end_date: dbPersonnel.collaborator_end_date || null,
    user_category_id: dbPersonnel.user_category_id || null,
    user_id: dbPersonnel.user_id || null,
  };
};

const determineExperienceLevel = (title) => {
  if (!title) return "Mid-Level";
  const t = title.toLowerCase();
  if (t.includes("senior") || t.includes("lead") || t.includes("principal"))
    return "Senior";
  if (t.includes("junior") || t.includes("associate")) return "Junior";
  if (t.includes("executive") || t.includes("director") || t.includes("vp"))
    return "Executive";
  return "Mid-Level";
};

const normalizeExperienceLevel = (level) => {
  if (!level) return "Mid-Level";
  const map = {
    junior: "Junior",
    mid: "Mid-Level",
    "mid-level": "Mid-Level",
    mid_level: "Mid-Level",
    senior: "Senior",
    executive: "Executive",
    lead: "Senior",
  };
  return map[level.toLowerCase().replace(/\s/g, "-")] ?? level;
};

export const deleteTempMember = async (orgDocumentsDraftId) => {
  const { error: tempError } = await supabase
    .from("temp_user_profiles")
    .delete()
    .eq("org_documents_draft_id", orgDocumentsDraftId);

  if (tempError) throw tempError;

  const { error: draftError } = await supabase
    .from("org_documents_draft")
    .delete()
    .eq("id", orgDocumentsDraftId);

  if (draftError) throw draftError;
};
